import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DecisionCluster, type ClusterSelection } from './DecisionCluster'
import { clusterDecisions, type DecisionTypeGroup } from '@/lib/decision-utils'

interface DecisionGroupProps {
  group: DecisionTypeGroup
  focusedId: string | null
  onFocus: (id: string) => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
  approving: boolean
  rejecting: boolean
  onApproveAll: (ids: string[]) => void
  onRejectAll: (ids: string[]) => void
  batchPending: boolean
  /** Ids currently animating out after an approve/reject. */
  exitingIds?: Set<string>
  selection: ClusterSelection
}

type Armed = 'none' | 'approve' | 'reject'

/**
 * One decisionType section: a header with the type label, a count, a select-all
 * checkbox, and (when the group has pending items) two-step batch APPROVE ALL /
 * REJECT ALL buttons, followed by the decisions — folded into clusters of
 * near-identical items.
 */
export function DecisionGroup({
  group,
  focusedId,
  onFocus,
  onApprove,
  onReject,
  approving,
  rejecting,
  onApproveAll,
  onRejectAll,
  batchPending,
  exitingIds,
  selection,
}: DecisionGroupProps) {
  const [armed, setArmed] = useState<Armed>('none')
  const clusters = useMemo(() => clusterDecisions(group.decisions), [group.decisions])
  const pendingCount = group.pendingIds.length
  const hasPending = pendingCount > 0
  const allSel = selection.allSelected(group.pendingIds)
  const someSel = selection.someSelected(group.pendingIds)

  function fire(action: 'approve' | 'reject') {
    if (armed !== action) {
      setArmed(action)
      return
    }
    if (action === 'approve') onApproveAll(group.pendingIds)
    else onRejectAll(group.pendingIds)
    setArmed('none')
  }

  return (
    <div>
      {/* Group header */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-cmd-low px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {hasPending && (
            <Checkbox
              checked={allSel}
              indeterminate={someSel}
              onCheckedChange={(c) => selection.setMany(group.pendingIds, c)}
              aria-label={`Select all ${pendingCount} ${group.label} decisions`}
            />
          )}
          <h3 className="font-label text-[0.7rem] font-semibold tracking-widest text-white uppercase truncate">
            {group.label}
          </h3>
          <span className="font-mono text-[0.6rem] text-cmd-outline-variant tabular-nums">
            {group.decisions.length}
          </span>
          {hasPending && (
            <Badge variant="high" className="text-[9px] px-1 py-0">
              {pendingCount} PENDING
            </Badge>
          )}
        </div>

        {hasPending && (
          <div className="flex items-center gap-1">
            {armed !== 'none' && (
              <Button
                size="sm"
                variant="ghost"
                disabled={batchPending}
                onClick={() => setArmed('none')}
                className="font-label text-[0.65rem] tracking-wider"
              >
                CANCEL
              </Button>
            )}
            <Button
              size="sm"
              variant="success"
              disabled={batchPending}
              onClick={() => fire('approve')}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {armed === 'approve' ? `CONFIRM · APPROVE ${pendingCount}` : `APPROVE ALL (${pendingCount})`}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={batchPending}
              onClick={() => fire('reject')}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {armed === 'reject' ? `CONFIRM · REJECT ${pendingCount}` : `REJECT ALL (${pendingCount})`}
            </Button>
          </div>
        )}
      </div>

      {/* Clusters */}
      <div className="space-y-1 mt-1">
        {clusters.map((cluster) => (
          <DecisionCluster
            key={cluster.signature}
            cluster={cluster}
            focusedId={focusedId}
            onFocus={onFocus}
            onApprove={onApprove}
            onReject={onReject}
            approving={approving}
            rejecting={rejecting}
            onApproveAll={onApproveAll}
            onRejectAll={onRejectAll}
            batchPending={batchPending}
            exitingIds={exitingIds}
            selection={selection}
            forceExpanded={!!focusedId && cluster.decisions.some((d) => d.id === focusedId)}
          />
        ))}
      </div>
    </div>
  )
}
